import { unwrap } from "@sagot/api-client";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../shared/keys";
import type { AgentUpdate, EvalCaseIn, EvalSetIn, GenerateIn } from "../shared/types";
import { ACTIVE_RUN, RUN_POLL_MS } from "./eval-model";

const HISTORY_LIMIT = 20;
const CASES_LIMIT = 50;

export function useEvalSets(agentId: string, polling = false) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.evalSets(ws, agentId),
    queryFn: () => unwrap(api.GET("/v1/eval-sets", { params: { query: { agent_id: agentId } } })),
    retry: false,
    refetchInterval: polling ? RUN_POLL_MS : false,
  });
}

export function useRunHistory(setId: string | null, configOnly: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.evalRuns(ws, setId ?? "", configOnly),
    queryFn: () => unwrap(api.GET("/v1/eval-sets/{set_id}/runs", { params: { path: { set_id: setId ?? "" }, query: { config_changes_only: configOnly, limit: HISTORY_LIMIT } } })),
    enabled: Boolean(setId),
    refetchInterval: (query) => (query.state.data?.items.some((r) => ACTIVE_RUN.has(r.status)) ? RUN_POLL_MS : false),
  });
}

export function useRunCases(runId: string | null) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.evalResults(ws, runId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/eval-runs/{run_id}/cases", { params: { path: { run_id: runId ?? "" }, query: { status: "fail,partial" } } })),
    enabled: Boolean(runId),
  });
}

export function useCompare(a: string | null, b: string | null) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.evalCompare(ws, a ?? "", b ?? ""),
    queryFn: () => unwrap(api.GET("/v1/eval-runs/compare", { params: { query: { a: a ?? "", b: b ?? "" } } })),
    enabled: Boolean(a && b),
  });
}

export function useCases(setId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: buildKeys.evalCases(ws, setId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => unwrap(api.GET("/v1/eval-sets/{set_id}/cases", { params: { path: { set_id: setId }, query: { limit: CASES_LIMIT, ...(pageParam ? { cursor: pageParam } : {}) } } })),
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function useEvalMutations(agentId: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: ["ws", ws] as const, predicate: (q) => String(q.queryKey[2]).startsWith("eval") });
  const set = (id: string) => ({ params: { path: { set_id: id } } });
  return {
    createSet: useMutation({ mutationFn: (body: EvalSetIn) => unwrap(api.POST("/v1/eval-sets", { body })), onSuccess: refresh }),
    patchSet: useMutation({ mutationFn: ({ id, body }: { id: string; body: { schedule?: EvalSetIn["schedule"]; alert_on_drop?: boolean } }) => unwrap(api.PATCH("/v1/eval-sets/{set_id}", { ...set(id), body })), onSuccess: refresh }),
    deleteSet: useMutation({ mutationFn: (id: string) => unwrap(api.DELETE("/v1/eval-sets/{set_id}", set(id))), onSuccess: refresh }),
    run: useMutation({ mutationFn: (id: string) => unwrap(api.POST("/v1/eval-sets/{set_id}/run", set(id))), onSuccess: refresh }),
    runAll: useMutation({ mutationFn: () => unwrap(api.POST("/v1/eval-sets/run-all", { params: { query: { agent_id: agentId } } })), onSuccess: refresh }),
    importCsv: useMutation({ mutationFn: ({ id, csv, dryRun }: { id: string; csv: string; dryRun: boolean }) => unwrap(api.POST("/v1/eval-sets/{set_id}/import", { ...set(id), body: { csv, dry_run: dryRun } })), onSuccess: refresh }),
    generate: useMutation({ mutationFn: ({ id, body }: { id: string; body: GenerateIn }) => unwrap(api.POST("/v1/eval-sets/{set_id}/generate", { ...set(id), body })), onSuccess: refresh }),
    addCase: useMutation({ mutationFn: ({ id, body }: { id: string; body: EvalCaseIn }) => unwrap(api.POST("/v1/eval-sets/{set_id}/cases", { ...set(id), body })), onSuccess: refresh }),
    deleteCase: useMutation({ mutationFn: (caseId: string) => unwrap(api.DELETE("/v1/eval-cases/{case_id}", { params: { path: { case_id: caseId } } })), onSuccess: refresh }),
    accept: useMutation({ mutationFn: ({ caseId, resultId }: { caseId: string; resultId: string }) => unwrap(api.POST("/v1/eval-cases/{case_id}/accept", { params: { path: { case_id: caseId } }, body: { result_id: resultId } })), onSuccess: refresh }),
    applyConfig: useMutation({
      mutationFn: (body: AgentUpdate) => unwrap(api.PATCH("/v1/agents/{agent_id}", { params: { path: { agent_id: agentId } }, body })),
      onSuccess: () => client.invalidateQueries({ queryKey: buildKeys.agent(ws, agentId) }),
    }),
    exportRun: (runId: string) => unwrap(api.GET("/v1/eval-runs/{run_id}/export", { params: { path: { run_id: runId } }, parseAs: "text" })),
  };
}
